<script setup>
import {
  onMounted,
  useTemplateRef,
} from 'vue';
import debounce from 'lodash.debounce';
import {
  canBeResponsive,
  chartColors,
} from '@@/utils/ChartUtils';
import { isScreenLg } from '@@/utils/CommonUtils';
import { useLocationStore } from '@@/stores/Location';
import { useMetaStore } from '@@/stores/Meta';
import { useUserStore } from '@@/stores/User';

const props = defineProps({
  location: {
    type: Object,
    default: null,
  },
  isSnowView: {
    type: Boolean,
    default: true,
  },
  isWeatherView: {
    type: Boolean,
    default: false,
  },
});

const { $chart } = useNuxtApp();
const locationStore = useLocationStore();
const metaStore = useMetaStore();
const userStore = useUserStore();

let chart = null;
const loading = ref(true);
const forecastEnsemble = ref(null);

const modelGroups = computed(() => [
  ...forecastEnsemble.value?.groups.map((group) => ({
    label: (isScreenLg()) ? group.label : group.label_short,
    value: group.name,
    // tbd, move to api?
    disabled: group.disabled_fields.includes(activeField.value.name),
  })) ?? [],
]);

const days = [
  { label: '1 Day', value: 1 },
  { label: '3 Days', value: 3 },
  { label: '5 Days', value: 5 },
  { label: '10 Days', value: 10 },
];

const activeFieldName = ref([]);
const activeField = computed(() => forecastEnsemble.value?.fields.find((f) => f.name === activeFieldName.value[0]));
const activeIdx = ref(null);

const activeGroupName = ref(null);
const activeDays = ref(days.at(-1).value);

const activeGroup = computed(() => forecastEnsemble.value?.groups.find((group) => group.name === activeGroupName.value));
const primaryMember = computed(() => forecastEnsemble.value?.members.find((m) => m.is_primary && !m.is_ai));
const groupPrimaryMember = computed(() => {
  const primaryMemberId = activeGroup.value?.primary_member_id;

  if (!primaryMemberId) {
    return null;
  }

  return forecastEnsemble.value?.members.find((member) => member.id === primaryMemberId) ?? null;
});

const groupPrimary = (member) => {
  return member?.is_primary || groupPrimaryMember.value === member;
};

const showCompareStats = computed(() => !!groupPrimaryMember.value);
const showMaxMinStats = computed(() => !showCompareStats.value);
const supportArticleUrl = computed(() => metaStore
  .getAttributionDetailUrl('cloudnine', 'forecast_ensemble'));

const theme = computed(() => userStore.computedTheme);
const isDark = computed(() => theme.value === 'dark');
const units = computed(() => userStore.preferences.units);

const showComponent = computed(() => {
  // return userStore.isAllAccess;
  // temporarily allow all data for all users
  return true;
});

const hexColorToRgba = (hexColor, opacity) => {
  // ('#ffffff', 0.5) => 'rgba(255,255,255,0.5)'
  const rgb = hexColor
    .replace(/^#/, '') // rip leading # if present
    .match(/[a-f0-9]{2}/ig) // match on hex character pairs
    .map((val) => parseInt(val, 16)) // convert to decimal
    .join(',');

  return `rgba(${rgb},${opacity})`;
};

const getChartDataColors = (member) => {
  const dataTypeName = groupPrimary(member) ? 'primary' : 'member';

  return forecastEnsemble.value?.chart_display
    .data_types
    .find((d) => d.name === dataTypeName)
    .themes
    .find((t) => t.name === theme.value);
};

const getLineColor = (member) => {
  const colors = getChartDataColors(member);
  const isHighlighted = memberShouldHighlight(member);

  return (isHighlighted)
    ? colors?.color_foreground_highlight
    : colors?.color_foreground;
};

const getDatasetFromForecast = (forecast) => {
  return forecast.map((row, idx) => {
    const period = forecastEnsemble.value.periods[idx];
    const x = Date.parse(period.display_at);
    const y = row[activeFieldName.value[0]];

    return { x, y };
  });
};

const resetDatasetKeys = [
  'borderWidth', 'borderColor', 'order',
];

const memberShouldHighlight = (member) => {
  return activeGroup.value?.member_ids.includes(member?.id) ?? false;
};

const getDatasets = () => {
  const datasets = [];

  const primaryWeight = 5;
  const memberWeight = 1.25;

  forecastEnsemble.value.members.forEach((member, idx) => {
    const { forecast, label, item_opacity: itemOpacity } = member;

    const isGroupHighlight = memberShouldHighlight(member);
    const isGroupPrimary = groupPrimary(member);

    const opacity = isGroupHighlight ? Math.min(1, itemOpacity * 3) : itemOpacity;
    const weight = isGroupPrimary ? primaryWeight : memberWeight;

    const data = getDatasetFromForecast(forecast);

    const color = hexColorToRgba(
      getLineColor(member),
      (isDark.value) ? opacity * 1.25 : opacity,
      // itemOpacity,
    );

    const showLine = activeField.value.chart_type === 'line';
    const lineWeight = (showLine)
      ? weight
      : (isScreenLg()) ? weight * 2 : weight * 1.2;

    const lineProps = {
      data,
      forecastMemberIdx: idx,
      label: label,
      borderWidth: lineWeight,
      pointStyle: showLine ? false : 'circle',
      radius: showLine ? 0 : isScreenLg() ? 3 : 2,
      pointHoverBorderWidth: lineWeight,
      backgroundColor: color,
      borderColor: color,
      showLine,
      borderCapStyle: 'round',
      cubicInterpolationMode: 'monotone',
      order: idx + (isGroupHighlight ? 1 : 100),
      pointHitRadius: 20,
    };

    // duplicate these at _key for managing hover state later
    resetDatasetKeys.forEach((key) => {
      lineProps[`_${key}`] = lineProps[key];
    });

    datasets.push(lineProps);

    // const borderOpacity =
    if (isGroupPrimary) {
      // outline for primary
      datasets.push({
        ...lineProps,
        isPrimaryBorder: true,
        borderWidth: lineWeight + 2,
        borderColor: (isDark.value) ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.75)',
      });
    }
  });

  return datasets;
};

const getLabels = () => {
  const labels = [];
  let skipped = false;
  forecastEnsemble.value?.periods.forEach((period) => {
    if (period.chart_labels) {
      skipped = !skipped;
      if (isScreenLg() || skipped) {
        labels.push(period.chart_labels);
        return;
      }
    }

    labels.push('');
  });
  return labels;
};

const yValueRound = (value, maxValue) => {
  maxValue = Math.abs(maxValue);

  // always round temp to nearest 1
  if (activeField.value.name === 'temp') {
    return Math.round(value);
  }

  // round to nearest 100
  if (maxValue > 1000) {
    return Math.round(value / 100) * 100;
  }

  // round to nearest 10
  if (maxValue > 100) {
    return Math.round(value / 10) * 10;
  }

  // round to nearest 1
  if (maxValue > 10) {
    return Math.round(value);
  }

  // round to nearest 10th, preserving any trailing 0
  if (maxValue > 1) {
    return value.toFixed(1);
  }

  // max values between 0 and 1
  // round to nearest 100th, preserving any trailing 0s
  return value.toFixed(2);
};

const yValueLabel = (value, maxValue) => {
  if (value == null) {
    return null;
  }

  const formatted = (value > 1000)
    ? new Intl.NumberFormat().format(yValueRound(value, maxValue)) // just for a comma
    : yValueRound(value, maxValue);

  return `${formatted}${activeField.value.units_label_short ?? ''}`;
};

const yAxisLabel = (value, idx, ticks) => {
  const max = ticks.at(-1).value;
  return yValueLabel(value, max);
};

const getXAxis = () => {
  return {
    beginAtZero: false,
    grid: {
      display: false,
    },
    ticks: {
      autoSkip: false,
      color: chartColors.textDark(theme.value),
      font: {
        size: 11,
      },
      maxRotation: 0,
      minRotation: 0,
    },
  };
};

const getYAxis = (position = 'left') => {
  return {
    // beginAtZero: false,
    position,
    border: {
      dash({ index }) {
        return index === 0 ? false : [2, 2];
      },
      display: false,
      width: 0,
    },
    grid: {
      color: chartColors.borderColor(theme.value),
      display: true,
      drawTicks: false,
    },
    afterBuildTicks: function (scale) {
      // use ticks returned from api
      scale.ticks = activeField.value.range_steps.map((value) => ({ value }));
    },
    ticks: {
      callback: yAxisLabel,
      color: chartColors.textDark(theme.value),
      font: {
        size: 11,
      },
      padding: (position === 'left') ? 10 : 2,
      crossAlign: (position === 'left') ? 'near' : 'far',
    },
    // force 0 if its 0!
    min: activeField.value.range_min === 0 ? 0 : undefined,
    suggestedMin: activeField.value.range_min,
    suggestedMax: activeField.value.range_max,
  };
};

const updateConfigForYAxis = (config) => {
  if (isScreenLg()) {
    config.options.scales.yRight = {
      ...getYAxis('right'),
      border: {
        display: false,
      },
      grid: {
        display: false,
      },
      // Duplicate ticks from left y-axis on right y-axis
      // SEE: https://stackoverflow.com/questions/71151513/chart-js-show-duplicate-y-axis-on-left-and-right-sides
      afterBuildTicks: (axis) => {
        axis.ticks = [...axis.chart.scales.y.ticks];
        axis.min = axis.chart.scales.y.min;
        axis.max = axis.chart.scales.y.max;
      },
    };
  }
  else {
    delete config.options.scales.yRight;
  }
};

let firstResize = true;
const handleResize = async (resizedChart) => {
  if (firstResize) {
    // Ignore the first resize event since a one will be triggered by the initial chart render.
    firstResize = false;
    return;
  }

  resizedChart.config.data.labels = getLabels();
  updateConfigForYAxis(resizedChart.config);
  resizedChart.update('none');
};

const getDayNightShading = () => {
  // Use annotation plug-in to alternate background colors to distinguish day/night.
  // SEE: https://stackoverflow.com/questions/70686455/chart-js-alternating-background-color-for-ticks

  const chartTheme = forecastEnsemble.value.chart_display.themes.find((t) => t.name === theme.value);

  return {
    annotations: forecastEnsemble.value.periods
      .reduce((acc, period, index, periods) => {
        const [xMin, xMax] = (activeField.value.chart_type === 'line')
          // shading begins/ends at the boundaries of the time period
          ? [index, index + 1]
          // offset shading by half so that bubbles are fully within the day or night shading instead of straddling the boundary
          : [Math.max(0, index - 0.5), Math.min(index + 0.5, periods.length - 1)];

        // Don't draw a box for the last period if it's night since the box would be drawn
        // outside the chart behind the right y-axis.
        if (period.day_period === 'night' && xMax < periods.length) {
          acc.push({
            backgroundColor: chartTheme.color_background_alternate,
            borderWidth: 0,
            drawTime: 'beforeDraw',
            type: 'box',
            xMin,
            xMax,
            xScaleID: 'x',
            yScaleID: 'y',
          });
        }

        return acc;
      }, []),
    clip: false,
  };
};

const stats = ref({
  date: { label: '', value: null },
  min: { label: 'Min', value: null },
  med: { label: 'Med', value: null },
  max: { label: 'Max', value: null },
  primary: { label: '', value: null },
  compare: { label: '', value: null },
  comparePctChange: { label: '', value: null, style: [] },
});

const setStatsItem = (key, props = {}) => {
  Object.entries(props).forEach(([k, v]) => {
    stats.value[key][k] = v;
  });
};

let ttActiveDs = null; // not reactive for reasons
let ttActiveIdx = null;
let ttTimeoutId = null;
const renderTooltip = (context) => {
  const idx = context?.tooltip.dataPoints[0]?.dataIndex ?? activeIdx.value;
  const dsIndex = context?.tooltip.dataPoints[0]?.datasetIndex ?? null;
  const ds = (dsIndex !== null) ? chart.data.datasets[dsIndex] : null;
  const member = forecastEnsemble.value?.members[ds?.forecastMemberIdx];

  const hasUpdates = ttActiveDs !== ds || idx !== ttActiveIdx;
  const visible = context?.tooltip?.opacity === 1;
  // uncomment to only allow highlighting of selected group lines
  // const visible = context?.tooltip?.opacity === 1 && (!activeGroup.value || memberShouldHighlight(member));

  if (ttActiveDs && (!visible || ttActiveDs !== ds)) {
    resetDatasetKeys.forEach((key) => {
      ttActiveDs[key] = ttActiveDs[`_${key}`];
    });
    chart.update();
    ttActiveDs = null;
  }

  if (hasUpdates || tooltip.value.visible !== visible) {
    clearTimeout(ttTimeoutId);
    // delay hide to reduce flashy-ness
    ttTimeoutId = setTimeout(() => updateTooltip(context, visible, member), (!visible) ? 125 : 10);
  }

  if (!visible || !ds || !hasUpdates) {
    // nothing to do! it's already been done
    return;
  }

  ttActiveDs = ds;
  ttActiveIdx = idx;
  activeIdx.value = idx; // is this bad to set this here?
  ds.order = 0;
  ds.borderWidth = ds._borderWidth + 2;
  ds.borderColor = getChartDataColors(member).color_foreground_active;
  chart.update();
};

const tooltipRef = useTemplateRef('tooltipRef');
const tooltip = ref({
  activeMember: null,
  title: '',
  body: '',
  visible: false,
  left: 0,
  top: 0,
  opacity: 0,
  pointer: 'left',
});

const updateTooltip = async (context, visible, member) => {
  const tt = tooltip.value;
  tt.activeMember = member;

  if (!visible || !member) {
    tt.visible = false;
    return;
  }

  tt.title = member.annotation_label;
  tt.body = yValueLabel(
    member.forecast[activeIdx.value]?.[activeField.value.name],
    // Math.max(...activeField.value.stats_max),
    activeField.value.range_steps.at(-1),
  );

  const { offsetHeight, offsetWidth } = tooltipRef.value;
  const { caretX, caretY } = context.tooltip;

  // if (caretX + offsetWidth < chart.width - 50) {
  if (caretX < (offsetWidth + 50)) {
    tt.left = caretX + 10;
    tt.pointer = 'left';
  }
  else {
    tt.left = caretX - offsetWidth - 10;
    tt.pointer = 'right';
  }

  tt.top = caretY - (offsetHeight / 2) - 2;
  tt.visible = true;
};

const crosshairs = ref({
  xVisible: true,
  yVisible: false,
  yWidth: 0,
  yLeft: 0,
  x: 0,
  y: 0,
});

const crosshairXRef = useTemplateRef('crosshairXRef');
const crosshairYRef = useTemplateRef('crosshairYRef');

const updateStats = (chart, idx, forceUpdate = false) => {
  if (activeIdx.value === idx && !forceUpdate) {
    return;
  }

  crosshairs.value.x = chart.scales.x.getPixelForValue(idx);
  activeIdx.value = idx;

  const field = activeField.value;
  const period = forecastEnsemble.value.periods[idx];

  const maxValue = Math.max(...field.stats_max);
  const date = (activeField.value.is_cumulative)
    ? period.display_at_local_label
    : period.display_at_local_label_duration;
  const primary = primaryMember.value;
  const primaryVal = primary.forecast[idx][field.name];

  // temp, remove since we added day night shading, may add back?
  // const periodLabel = (period.day_period === 'day') ? 'Day' : 'Night';
  setStatsItem('date', { value: date, label: field.label });

  ['min', 'med', 'max'].forEach((key) => {
    setStatsItem(key, { value: yValueLabel(field[`stats_${key}`][idx], maxValue) });
  });

  setStatsItem('primary', { value: yValueLabel(primaryVal, maxValue), label: primary.label });

  if (!showCompareStats.value) {
    return;
  }

  const compare = groupPrimaryMember.value;
  const compareVal = compare.forecast[idx]?.[field.name];
  setStatsItem('compare', { value: compareVal ? yValueLabel(compareVal, maxValue) : '--', label: compare.label });

  const pctCompare = (compareVal > 0 && primaryVal > 0)
    ? ((compareVal - primaryVal) / primaryVal) * 100
    : null;

  const formatted = (pctCompare !== null)
    ? Math.abs(pctCompare < 10) ? pctCompare.toFixed(1) : Math.round(pctCompare)
    : '--';

  const style = ['pctChange'];
  if (pctCompare === null) {
    style.push('neutral');
  }
  else if (pctCompare > 0) {
    style.push('positive');
  }
  else {
    style.push('negative');
  }

  setStatsItem('comparePctChange', {
    value: `${(formatted > 0) ? '+' : ''}${formatted}${pctCompare !== null ? '%' : ''}`,
    label: `vs ${stats.value.primary.label}`,
    style,
  });
};

const canvasRef = useTemplateRef('canvasRef');
const renderChart = () => {
  if (!forecastEnsemble.value) {
    return;
  }

  if (chart) {
    chart.destroy();
    chart = null;
  }

  // reset data display
  Object.keys(stats.value).forEach((key) => stats.value[key].value = null);

  const config = {
    data: {
      datasets: getDatasets(),
      labels: getLabels(),
    },
    options: {
      clip: false,
      layout: {
        padding: {
          bottom: 4,
          left: 0,
          right: 6,
          top: 4,
        },
      },
      maintainAspectRatio: false,
      // Debounce the onresize handler since resizeDelay doesn't seem to do this.
      // SEE: https://www.chartjs.org/docs/latest/configuration/responsive.html#configuration-options
      onResize: debounce(handleResize, 500),
      onHover(event, activeElements, chart) {
        const canvasPos = $chart.helpers.getRelativePosition(event, chart);
        const dataX = chart.scales.x.getValueForPixel(canvasPos.x);
        // handle the y here, and the x in stats (since thats called on chart load too)
        const overhang = 6;
        const multiple = isScreenLg() ? 2 : 1;
        crosshairs.value.y = canvasPos.y;
        crosshairs.value.yLeft = chart.scales.y.width - overhang;
        crosshairs.value.yWidth = chart.width - (chart.scales.y.width * multiple) + (overhang * multiple);
        crosshairs.value.yVisible = true;
        updateStats(chart, dataX);
      },
      plugins: {
        annotation: getDayNightShading(),
        datalabels: {
          display: false,
        },
        legend: null,
        title: {
          display: false,
        },
        tooltip: {
          axis: 'xy',
          enabled: false,
          external: debounce(renderTooltip, 25),
          intersect: true,
          mode: 'nearest',
        },
      },
      scales: {
        x: getXAxis(),
        y: getYAxis(),
      },
    },
    plugins: [{
      afterRender(chart) {
        updateStats(chart, activeIdx.value, true);
      },
      beforeEvent(chart, args) {
        if (args.event.type === 'mouseout') {
          crosshairs.value.yVisible = false;
        }
      },
    }],
    responsive: canBeResponsive(),
    type: 'line',
  };

  updateConfigForYAxis(config);

  // Reset firstResize to true before creating the chart so the initial resize event is ignored.
  firstResize = true;
  chart = new $chart(canvasRef.value, config);
};

const fetchAndRenderChart = async () => {
  loading.value = true;
  forecastEnsemble.value = null;

  if (chart) {
    chart.destroy();
    chart = null;
  }

  try {
    const response = await locationStore.fetchForecastMembers({
      slug: props.location.slug,
      units: units.value,
      days: activeDays.value,
    });

    forecastEnsemble.value = response?.forecast_ensemble ?? null;

    // sort mutates
    forecastEnsemble.value.fields.sort(
      props.isWeatherView
        ? (a, b) => a.sort_weight - b.sort_weight
        : (a, b) => a.sort_weight_snow - b.sort_weight_snow,
    );

    // default to the end of the period
    activeIdx.value = forecastEnsemble.value.periods.length - 1;

    if (!activeFieldName.value[0]) {
      activeFieldName.value[0] = forecastEnsemble.value.fields[0].name;
    }

    if (!activeGroupName.value) {
      activeGroupName.value = forecastEnsemble.value.groups.find((group) => group.is_default).name;
    }
    renderChart();
  }
  catch (e) {
    console.error(e);
    forecastEnsemble.value = null;

    const { $toast } = useNuxtApp();
    const message = e?.data?.error || '';

    $toast.open({
      message: 'Error fetching forecast range data!',
      type: 'error',
    });

    console.warn(`Error fetching forecast range data! ${message}`);
  }

  loading.value = false;
};

if (showComponent.value) {
  onMounted(fetchAndRenderChart);
  watch(() => theme.value, renderChart);
  watch(() => units.value, fetchAndRenderChart);
}
</script>

<template>
  <Card
    v-if="showComponent"
    :has-body-padding-small="true"
    header-class-names="tw-flex tw-justify-flex-start tw-items-center tw-px-2 tw-py-4"
    :card-class-names="$style.main"
  >
    <template #header>
      <h2 class="section-title heading-color tw-pr-1.5">
        Forecast Range
      </h2>
      <ProPreviewBadge />
    </template>
    <template #body>
      <LoadingIndicator
        v-if="loading"
        class="tw-absolute"
      />
      <p
        v-else-if="!forecastEnsemble"
        class="tw-flex tw-justify-center tw-items-center tw-w-full tw-h-full tw-absolute"
      >
        <span class="tw-font-bold error-message-color">
          Forecasts Range data not available.
        </span>
      </p>
      <div
        :class="$style.layout"
      >
        <ClientOnly>
          <div
            v-if="forecastEnsemble"
            class="lg:tw-border-r lg:tw-pr-2 lg:tw--mr-1 tw-whitespace-nowrap tw-flex"
            :class="$style.selectors"
            style="border-color: var(--border-color);"
          >
            <select
              v-model="activeGroupName"
              :class="$style.minimal"
              class="tw-mr-2 tw-w-3/5 lg:tw-w-2/3"
              @change="renderChart"
            >
              <option
                v-for="group in modelGroups"
                :key="group.value"
                :value="group.value"
                :disabled="group.disabled"
              >
                {{ group.label }}
                {{ group.disabled ? 'N/A' : '' }}
              </option>
            </select>

            <select
              v-model="activeDays"
              :class="$style.minimal"
              class="tw-w-2/5 lg:tw-w-1/3"
              @change="fetchAndRenderChart"
            >
              <option
                v-for="item in days"
                :key="item.value"
                :value="item.value"
              >
                {{ item.label }}
              </option>
            </select>
          </div>

          <div
            v-if="forecastEnsemble"
            :class="$style.chips"
            class="tw-overflow-hidden tw-flex tw-z-10"
          >
            <Chips
              v-model="activeFieldName"
              class="tw-mt-1"
              v-bind="{
                canShowChipActiveIcon: false,
                multiple: false,
                noWrap: true,
                secondary: false,
                selectAllOptionValue: '',
                shouldScrollActiveElementIntoView: true,
              }"
              :options="forecastEnsemble.fields.map((f) => ({
                label: f.label,
                value: f.name,
              }))"
              @update:model-value="renderChart"
            />
          </div>

          <div
            v-show="forecastEnsemble"
            :class="$style.chart"
          >
            <canvas ref="canvasRef" />
            <div
              ref="crosshairXRef"
              :class="[$style.hoverLine, $style.x]"
              :style="{
                left: `${crosshairs.x}px`,
              }"
            />
            <div
              ref="crosshairYRef"
              :class="[$style.hoverLine, $style.y]"
              :style="{
                display: (crosshairs.yVisible) ? 'block' : 'none',
                left: `${crosshairs.yLeft}px`,
                top: `${crosshairs.y}px`,
                width: `${crosshairs.yWidth}px`,
              }"
            />
            <div
              ref="tooltipRef"
              :class="[$style.tooltip, $style[tooltip.pointer]]"
              class="tw-text-center"
              :style="{
                left: `${tooltip.left}px`,
                top: `${tooltip.top}px`,
                opacity: (tooltip.visible) ? 0.9 : 0,
              }"
            >
              <div
                v-if="tooltip.activeMember"
                class="tw-whitespace-nowrap tw-text-xs"
              >
                <div
                  :class="[$style.memberIndicator, groupPrimary(tooltip.activeMember) && $style.primaryIndicator]"
                  :style="`background-color: ${getLineColor(tooltip.activeMember)};`"
                />
                {{ tooltip.title }}
              </div>
              <div
                class="tw-text-lg tw-font-bold tw-my-1"
                style="color: var(--text-darkest)"
              >
                {{ tooltip.body }}
              </div>
              <div class="tw-text-[0.75rem] tw-whitespace-nowrap tw-font-normal">
                {{ stats.date.value }}
              </div>
              <div :class="$style.tooltipPointer" />
            </div>
          </div>

          <table
            v-if="forecastEnsemble"
            class="tw-text-xs tw-w-full"
            :class="$style.stats"
          >
            <thead>
              <tr>
                <th class="tw-text-sm tw-font-bold">
                  {{ stats.date.label }}
                </th>
                <th>{{ stats.date.value }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="showMaxMinStats">
                <td>
                  <div
                    :class="$style.memberIndicator"
                    :style="`background-color: ${getLineColor()};`"
                  />
                  {{ stats.max.label }}
                </td>
                <td>{{ stats.max.value }}</td>
              </tr>

              <tr>
                <td>
                  <div
                    :class="[$style.memberIndicator, $style.primaryIndicator]"
                    :style="`background-color: ${getLineColor(primaryMember)};`"
                  />
                  <span class="tw-text-sm">{{ stats.primary.label }}</span>
                </td>
                <td>
                  <strong class="tw-text-base">{{ stats.primary.value }}</strong>
                </td>
              </tr>

              <tr v-if="showCompareStats">
                <td>
                  <div
                    :class="[$style.memberIndicator, $style.primaryIndicator]"
                    :style="`background-color: ${getLineColor(groupPrimaryMember)};`"
                  />
                  <span class="tw-text-sm">{{ stats.compare.label }}</span>
                </td>
                <td>
                  <span
                    v-if="activeField.show_comparison_pct"
                    :class="stats.comparePctChange.style.map((s) => $style[s])"
                    class="tw-w-20 tw-px-2 tw-text-center tw-mr-2"
                  >{{ stats.comparePctChange.value }}</span>
                  <span class="tw-inline-block tw-whitespace-nowrap tw-min-w-12">
                    <strong class="tw-text-base">{{ stats.compare.value }}</strong>
                  </span>
                </td>
              </tr>

              <tr v-if="showMaxMinStats">
                <td>
                  <div
                    :class="[$style.memberIndicator]"
                    :style="`background-color: ${getLineColor()};`"
                  />
                  {{ stats.min.label }}
                </td>
                <td>
                  {{ stats.min.value }}
                </td>
              </tr>
            </tbody>
          </table>

          <div
            v-if="forecastEnsemble"
            :class="$style.disclaimers"
          >
            <p
              class="tw-text-xs tw-mt-4 lg:tw-mt-2"
              style="color: var(--text-light);"
            >
              <span
                :class="[$style.memberIndicator, $style.primaryIndicator, 'tw-mr-0']"
                :style="`background-color: ${getLineColor(primaryMember)};`"
              />
              {{ primaryMember.disclaimers.join(' ') }}
            </p>
            <p
              v-if="showCompareStats"
              class="tw-text-xs tw-mt-2"
              style="color: var(--text-light);"
            >
              <span
                :class="[$style.memberIndicator, $style.primaryIndicator, 'tw-mr-0']"
                :style="`background-color: ${getLineColor(groupPrimaryMember)};`"
              />
              {{ groupPrimaryMember.disclaimers.join(' ') }}
              <a
                class="link-color-brand"
                target="_blank"
                :href="groupPrimaryMember.disclaimer_url"
              >Learn More</a>
            </p>
          </div>
        </ClientOnly>
      </div>
      <div
        v-if="forecastEnsemble && supportArticleUrl"
        class="tw-mt-4 tw-mb-1 tw-font-normal tw-text-sm tw-text-center"
      >
        <a
          class="hover:tw-underline link-color-brand"
          :href="supportArticleUrl"
          rel="noopener noreferrer"
          target="_blank"
        >
          How to read this data
          <font-awesome-icon
            class="tw-ml-1"
            :icon="['far', 'info-circle']"
          />
        </a>
      </div>
    </template>
  </Card>
</template>

<style module>
.main {
  min-height: 22.2rem;
}

.layout {
  display: grid;
  gap: 10px;
  grid-template-areas:
    "selectors chips chips"
    "chart chart stats"
    "chart chart disclaimers";
  grid-template-columns: 1.5fr 1fr 1.2fr;
  margin: auto;
}

/* tailwind lg breakpoint 64rem */
@media (max-width: 64rem) {
  .main {
    min-height: 37.2rem;
  }

  .layout {
    gap: 4px;
    grid-template-areas:
      "chips"
      "selectors"
      "chart"
      "stats"
      "disclaimers";
    grid-template-columns: 1fr;
  }
}

.selectors {
  grid-area: selectors;

  select {
    appearance: none;
    background-color: rgb(var(--button-secondary-background-rgb));
    border: 1px solid var(--border-color);
    border-radius: 6px;
    box-sizing: border-box;
    display: inline-block;
    font: inherit;
    font-size: 1rem;
    line-height: 1.5em;
    margin-top: 4px;
    padding: 0.35em 1.75em 0.35em 1em;
  }

  select:hover {
    background-color: var(--button-secondary-background-hover);
  }

  select.minimal {
    background-image:
      linear-gradient(45deg, transparent 50%, gray 50%),
      linear-gradient(135deg, gray 50%, transparent 50%);
    background-position:
      calc(100% - 20px) 1em,
      calc(100% - 15px) 1em;
    background-repeat: no-repeat;
    background-size:
      5px 5px,
      5px 5px,
      1px 1.5em;
  }

  select.minimal:focus {
    background-image:
    linear-gradient(45deg, var(--light-blue) 50%, transparent 50%),
    linear-gradient(135deg, transparent 50%, var(--light-blue) 50%);
    background-position:
    calc(100% - 15px) 1em,
    calc(100% - 20px) 1em;
    background-repeat: no-repeat;
    background-size:
      5px 5px,
      5px 5px,
      1px 1.5em;
    border-color: var(--light-blue);
    outline: 0;
  }
}

.chips {
  grid-area: chips;
}

.disclaimers {
  grid-area: disclaimers;
}

.chart {
  grid-area: chart;
  height: 15rem;

  /* https://github.com/chartjs/Chart.js/issues/4156 */
  min-width: 0;
  position: relative;
  touch-action: none;

  .hoverLine {
    left: 0;
    opacity: 0.65;
    pointer-events: none;
    position: absolute;
    top: 0;

    &.x {
      border-right: 1px solid var(--text-regular);
      height: 13.0rem;
      margin-top: 0.5rem;
      width: 0;
    }

    /* stylelint-disable */
    &.y {
      border-top: 1px solid var(--text-regular);
      height: 0;
    }
  }

  .tooltip {
    background: var(--input-background-color);
    border: 1px solid var(--text-light);
    border-radius: 6px;
    color: var(--text-regular);
    left: 400px;
    opacity: 0;
    padding: 4px 20px;
    pointer-events: none;
    position: absolute;
    top: 40px;
    transition: all 0.1s ease, opacity 0.35s ease;
    width: 180px;
    z-index: 10;

    .tooltip-pointer {
      background: var(--input-background-color);
      border-radius: 0;
      border-right: 1px solid var(--text-light);
      border-top: 1px solid var(--text-light);
      border-top-right-radius: 2px;
      height: 8px;
      margin-top: -4px;
      position: absolute;
      top: 50%;
      width: 8px;

    }

    &.right .tooltip-pointer {
      right: -5px;
      transform: rotate(45deg);
    }

    &.left .tooltip-pointer {
      left: -5px;
      transform: rotate(225deg);
    }
  }
}

.stats {
  grid-area: stats;

  th {
    font-weight: normal;
    line-height: 1.5rem;
    padding: 0 0 2px 6px;
    text-align: left;
    white-space: nowrap;
  }

  td {
    border-bottom: 1px solid var(--border-color);
    line-height: 1.7rem;
    padding: 6px 10px;
    white-space: nowrap;
  }

  td:last-child, th:last-child {
    padding-right: 10px;
    text-align: right;
  }

  tr:last-child td {
    border-bottom: none;
  }

}

.member-indicator {
  border: 1px solid #ccc;
  border-radius: 10px;
  display: inline-block;
  height: 0.6rem;
  margin-right: 0.2rem;
  opacity: 0.5;
  width: 0.8rem;
}

.primary-indicator {
  opacity: 1;
}

.primary-ai-stat {
  border: 1px solid var(--border-color);
  border-radius: 4px;
  transform: translateY(-3px);
}

.pct-change {
  border: 1px solid var(--border-color);
  border-radius: 4px;
  display: inline-block;
  font-size: 12px;
  line-height: 1rem;
  margin-left: 4px;
  padding: 2px 4px;
  text-align: center;
  white-space: nowrap;
}

.positive {
  background-color: #DFF2E1;
  color: #228B22;
  font-weight: bold;
}

.negative {
  background-color: #F8E1E4;
  color: #B22222;
  font-weight: bold;
}

.neutral {
  background-color: var(--box-color);
  color: var(--text-light);
}
</style>
